import React, { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { FaCogs, FaDonate } from 'react-icons/fa';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { string } from 'yup';

import BreadCrumb from '~/components/BreadCrumb';
import DefaultInput from '~/components/Inputs/Default';
import PercentageInput from '~/components/Inputs/Percentage';
import Toggle from '~/components/Toggle';
import SaveButton from '~/components/Buttons/Save';
import ExitButton from '~/components/Buttons/Exit';
import ConfirmProcedureDialog from '~/components/Dialogs/ConfirmProcedure';
import ConfirmExitDialog from '~/components/Dialogs/ConfirmExit';

import { useToast } from '~/hooks/toast';

import api from '~/services/api';

import {
  Container,
  Content,
  OneFourthWrapper,
  HalfWrapper,
  ButtonContainer,
} from './styles';

interface IFormTaxDeductionType {
  name: string;
  active: boolean;
  percentage: number;
}

interface ILocation {
  id: string;
  name: string;
  active: boolean;
  percentage: number;
}

const schema = Yup.object().shape({
  name: Yup.string().required('Campo obrigatório'),
  percentage: Yup.string().when((value: string) => {
    if (Number.isNaN(Number(parseFloat(value)))) {
      return string().required('Conteúdo inválido');
    }

    return string().notRequired();
  }),
});

const New: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { state } = useLocation<ILocation>();
  const [isOpenConfirmProcedure, setIsOpenConfirmProcedure] = useState(false);
  const [isOpenExit, setIsOpenExit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { handleSubmit, register, getValues, setValue, control, errors } =
    useForm<IFormTaxDeductionType>({
      resolver: yupResolver(schema),
      defaultValues: {
        active: state?.active,
      },
    });

  const submitForm = useCallback(
    async (data: IFormTaxDeductionType) => {
      try {
        setIsSaving(true);

        if (state === undefined) {
          await api.post('/configurations/taxdeductiontypes', data);

          addToast({
            type: 'success',
            title: 'Cadastro realizado!',
            description: 'Tipo de abatimento fiscal cadastrado com sucesso.',
          });
        } else {
          await api.put(`/configurations/taxdeductiontypes`, {
            id: state.id,
            ...data,
          });

          addToast({
            type: 'success',
            title: 'Cadastro atualizado!',
            description: 'Tipo de abatimento fiscal atualizado com sucesso.',
          });
        }

        history.push('/configurations/taxdeductiontypes');
      } catch (error: any) {
        setIsSaving(false);

        addToast({
          type: 'error',
          title: 'Ocorreu um erro!',
          description: error.response.data.message,
        });
      }
    },
    [addToast, history, state],
  );

  function onConfirmProcedure(): void {
    const percentageValue = getValues('percentage');

    let percentageConv = percentageValue.toString();
    percentageConv = percentageConv.replaceAll('%', '');
    percentageConv = percentageConv.replaceAll('.', '');
    percentageConv = percentageConv.replaceAll(',', '.');

    if (!Number.isNaN(Number(parseFloat(percentageConv)))) {
      setValue('percentage', parseFloat(percentageConv));
    }

    handleSubmit(submitForm)();
  }

  const onExit = useCallback(() => {
    history.push('/configurations/taxdeductiontypes');
  }, [history]);

  return (
    <Container>
      <BreadCrumb
        main="Configurações"
        path="/configurations/main"
        firstChildPath="/configurations/taxdeductiontypes"
        firstChild="Tipos de Abatimentos Fiscais"
        secondChild={
          state === undefined
            ? 'Novo Tipo de Abatimento Fiscal'
            : 'Edição de Tipo de Abatimento Fiscal'
        }
        mainIcon={FaCogs}
        firstChildIcon={FaDonate}
        secondChildIcon={FaDonate}
        mainColored
        firstChildColored
      />

      <form onSubmit={handleSubmit(submitForm)}>
        <Content style={{ maxWidth: 700 }}>
          <HalfWrapper>
            <DefaultInput
              name="name"
              labelFor="name"
              labelText="Nome"
              type="text"
              defaultValue={state?.name}
              error={errors?.name?.message}
              ref={register}
              maxLength={60}
            />
          </HalfWrapper>

          <OneFourthWrapper>
            <Controller
              render={props => (
                <Toggle
                  onChange={e => props.onChange(e)}
                  checked={props.value}
                />
              )}
              name="active"
              defaultValue
              control={control}
            />
          </OneFourthWrapper>

          <OneFourthWrapper>
            <PercentageInput
              name="percentage"
              labelFor="percentage"
              labelText="Percentual"
              defaultValue={state?.percentage}
              error={errors?.percentage?.message}
              ref={register}
            />
          </OneFourthWrapper>
        </Content>

        <ButtonContainer>
          <SaveButton
            type="button"
            processing={isSaving}
            disabled={isSaving}
            onClick={() => {
              setIsOpenConfirmProcedure(true);
            }}
          />

          <ExitButton
            style={{ marginLeft: 10 }}
            type="button"
            disabled={isSaving}
            onClick={() => setIsOpenExit(true)}
          />
        </ButtonContainer>
      </form>

      <ConfirmProcedureDialog
        isOpen={isOpenConfirmProcedure}
        setIsOpen={setIsOpenConfirmProcedure}
        onConfirm={onConfirmProcedure}
        title="Confirma gravação do tipo de abatimento fiscal?"
        subTitle=""
        opConfirm="Sim"
        type=""
      />

      <ConfirmExitDialog
        isOpen={isOpenExit}
        setIsOpen={setIsOpenExit}
        onExit={onExit}
      />
    </Container>
  );
};

export default New;
